/*
 * Sheets as history entries (Saif, 22 Oct 2026): a sheet the customer can land on by itself (Rewards, Earn, the card's
 * menu, the code pop-up, top-up, store credit, the collection time, the spin rules) is a history entry, `?sheet=<kind>`
 * on the current page. A page reached from one (Invite a friend, Activity, the games, the terms) is a further entry, so
 * Back, the left-edge swipe and the button all return to the sheet; closing a sheet pops its entry, so a later Back
 * doesn't reopen it. Sheets that carry something (a reward to pick, an order to save) aren't entries.
 */

export const HISTORY_SHEETS = ['rewards', 'earn', 'cardMenu', 'code', 'topup', 'credit', 'pickup', 'spinRules', 'emailList'] as const;
export type HistorySheetKind = (typeof HISTORY_SHEETS)[number];

export const isHistorySheet = (kind: string | null | undefined): kind is HistorySheetKind => !!kind && (HISTORY_SHEETS as readonly string[]).includes(kind);

/** The sheet named by the address, or null. */
export function sheetInSearch(search: URLSearchParams): HistorySheetKind | null {
  const k = search.get('sheet');
  return isHistorySheet(k) ? k : null;
}

/** The address that opens `kind` over `path`, keeping nothing else from the query (the sheet is the only thing an address carries). */
export const sheetPath = (path: string, kind: HistorySheetKind): string => `${path}?sheet=${kind}`;

/**
 * What to do when the address changes (Back, a push, a reload): open the sheet it names, close a history sheet it no
 * longer names, or nothing. `current` is the sheet showing now.
 */
export function sheetSync(search: URLSearchParams, current: string | null): { open: HistorySheetKind } | { close: true } | null {
  const named = sheetInSearch(search);
  if (named && named !== current) return { open: named };
  if (!named && isHistorySheet(current)) return { close: true };
  return null;
}

/** What closing the sheet showing now should do: pop the address entry it owns, or just hide it. */
export function closeAction(search: URLSearchParams, current: string | null): 'back' | 'hide' {
  return sheetInSearch(search) && isHistorySheet(current) ? 'back' : 'hide';
}
